import React, { useState, useRef, useEffect, useMemo } from "react";
import geojsonExtent from "@mapbox/geojson-extent";
import useMobileDetect from "use-mobile-detect-hook";
import Cookies from "js-cookie";
import { debounce } from "lodash";

import maplibregl from "!maplibre-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "maplibre-gl/dist/maplibre-gl.css";

import BaseSearchBox from "./BaseSearchBox";
import LayerToggle from "./maps/LayerToggle";
import Legend from "./maps/Legend";
import CoverageTimeline from "./maps/CoverageTimeline";
import PrintSelector from "./maps/PrintSelector";
import { prepareFootprints, sparseCoverageIds } from "./maps/viewportCoverage";
import {
  RESIZE_CURSORS,
  footprintCoverage,
  lngLatBounds,
  printAreaGeoJSON,
  resizeSquare,
  squareAroundPoint,
  squareFromDrag,
  squareContains,
  squareHandleAt,
  squareSideMiles,
  toMercator,
  fromMercator,
} from "./maps/printArea";

import useCoverageSelect from "../shared/useCoverageSelect";
import packEventValue from "../shared/packEventValue";
import { addCoverage, loadCoverages } from "./maps/layers/coverages";
import { MAX_MAP_CELLS, cellsInBounds, fetchCell } from "./maps/mapCells";

import {
  ramp as countiesRamp,
  source as countiesSource,
  layer as countiesLayer,
} from "./maps/layers/counties";

import {
  ramp as coverageRamp,
  source as coverageSource,
  layer as coverageLayer,
} from "./maps/layers/coverage";

import {
  source as countySource,
  layer as countyLayer,
} from "./maps/layers/county";

const CountyMap = ({
  countyGeoJSON,
  searchPath,
  unmappedPath,
  unmappedRollsCount,
  coverages,
  cartItemsPath,
  highAltitudePrintCouponCode,
}) => {
  const ZOOM_SEARCH_ENABLE = 10;
  const HID_COUNTY_MAP_TIP_COOKIE_NAME = "hid_county_map_tip";
  const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] };
  const MIN_DRAWN_SQUARE_MILES = 0.05;
  const PLAY_INTERVAL_MS = 5000;

  const detectMobile = useMobileDetect();

  const mapContainer = useRef(null);
  const map = useRef(null);

  const [hideInstructions, setHideInstructions] = useState(
    Cookies.get(HID_COUNTY_MAP_TIP_COOKIE_NAME) === "true",
  );
  const [lng, setLng] = useState(null);
  const [lat, setLat] = useState(null);
  const [loading, setLoading] = useState(true);

  const [displayLng, setDisplayLng] = useState(null);
  const [displayLat, setDisplayLat] = useState(null);
  const [displayZoom, setDisplayZoom] = useState(null);
  const [displayQ, setDisplayQ] = useState(null);
  const [imagesCount, setImagesCount] = useState(null);
  const [resultsDismissed, setResultsDismissed] = useState(false);
  const resultsDismissedRef = useRef(false);

  const [coverageActive, setCoverageActive] = useState(null);
  const handleCoverageClick = () => setCoverageActive(!coverageActive);

  const [roadsActive, setRoadsActive] = useState(true);
  const handleRoadsActiveClick = () => setRoadsActive(!roadsActive);

  const [satelliteActive, setSatelliteActive] = useState(null);
  const [activeCoverage, setActiveCoverage] = useState(null);
  const [sparseCoverages, setSparseCoverages] = useState([]);
  const [denseCoverages, setDenseCoverages] = useState([]);
  // Coverages and footprint pieces for the map cells in view, loaded as the
  // map pans so neighboring counties' imagery shows up too
  const [cellCoverages, setCellCoverages] = useState({});
  const [footprints, setFootprints] = useState([]);
  const footprintsRef = useRef([]);
  const requestedCells = useRef(new Set());
  const loadedCells = useRef(new Set());

  const allCoverages = useMemo(() => {
    const byId = new Map();
    [...(coverages || []), ...Object.values(cellCoverages)].forEach((c) => {
      if (!byId.has(c.id)) byId.set(c.id, c);
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name)),
    );
  }, [coverages, cellCoverages]);
  const handleCoverageSelect = (coverage) => {
    if (!coverage) stopPlaying("imagery_off");
    setSatelliteActive(!!coverage);
    setActiveCoverage(coverage);
  };
  const handleTimelineSelect = (coverage) => {
    stopPlaying("year_click");
    handleCoverageSelect(coverage);
  };
  const handleSatelliteClick = () => {
    _vaTrack("map.imagery_toggled", {
      event_value: satelliteActive ? "off" : "on",
    });
    handleCoverageSelect(satelliteActive ? null : "Modern");
  };

  const displayedCoverage = useCoverageSelect(
    map,
    satelliteActive,
    activeCoverage,
    setActiveCoverage,
  );

  const [printOpen, setPrintOpen] = useState(false);
  const [printMode, setPrintMode] = useState("point");
  const [printCenter, setPrintCenter] = useState(null);
  const [printDistance, setPrintDistance] = useState(1);
  const [drawnBbox, setDrawnBbox] = useState(null);
  const printState = useRef({});
  const printMarker = useRef(null);

  const printCoverage = allCoverages.find(
    (c) => String(c.id) === String(displayedCoverage),
  );
  const printBbox =
    printMode === "point"
      ? printCenter && squareAroundPoint(printCenter, printDistance)
      : drawnBbox;
  printState.current = {
    open: printOpen,
    mode: printMode,
    bbox: printBbox,
    center: printCenter,
  };

  const trackPrintAdjusted = (how) =>
    _vaTrack("map.high_altitude_print_adjusted", { event_value: how });

  const handlePrintModeChange = (mode) => {
    _vaTrack("map.high_altitude_print_mode", { event_value: mode });
    setPrintMode(mode);
  };

  const handlePrintDistanceChange = (distance) => {
    _vaTrack("map.high_altitude_print_distance", { event_value: distance });
    setPrintDistance(distance);
  };

  const handlePrintToggle = () => {
    if (printOpen) {
      setPrintOpen(false);
      return;
    }

    // Start from the middle of the map unless the last print area is still in
    // view, e.g. after panning away or searching for an address
    const view = map.current.getBounds();
    const center = map.current.getCenter().toArray();
    const inView = (bbox) =>
      view.contains(
        fromMercator([(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2]),
      );

    stopPlaying("print");
    if (!printCenter || !view.contains(printCenter)) setPrintCenter(center);
    if (!drawnBbox || !inView(drawnBbox)) {
      setDrawnBbox(squareAroundPoint(center, printDistance));
    }
    setPrintOpen(true);
    _vaTrack("map.high_altitude_print_opened", {
      event_value: printCoverage ? printCoverage.id : "none",
    });
  };

  const [playing, setPlaying] = useState(false);
  const playSteps = useRef(0);

  const stopPlaying = (reason) => {
    if (!playing) return;

    _vaTrack("map.imagery_play_stopped", {
      event_value: packEventValue({ reason, steps: playSteps.current }),
    });
    setPlaying(false);
  };
  const playSequence = [
    "Modern",
    ...allCoverages.map((c) => c.id).reverse(),
  ].filter((id) => !sparseCoverages.includes(id));
  const handleTogglePlay = () => {
    if (playing) {
      stopPlaying("pause");
      return;
    }

    const start =
      satelliteActive && playSequence.includes(activeCoverage)
        ? activeCoverage
        : playSequence[0];
    if (start !== activeCoverage) handleCoverageSelect(start);
    playSteps.current = 0;
    setPlaying(true);
    _vaTrack("map.imagery_play_started", { event_value: start });
  };

  useEffect(() => {
    if (!playing) return;
    if (displayedCoverage !== activeCoverage) return;

    const timeout = setTimeout(() => {
      const next =
        playSequence[
          (playSequence.indexOf(activeCoverage) + 1) % playSequence.length
        ];
      playSteps.current += 1;
      handleCoverageSelect(next);
    }, PLAY_INTERVAL_MS);

    return () => clearTimeout(timeout);
  }, [playing, activeCoverage, displayedCoverage, sparseCoverages]);

  const searchCenter = async () => {
    if (!map.current) return;

    const center = map.current.getCenter();
    const zoom = map.current.getZoom();

    setDisplayLng(center.lng.toFixed(4));
    setDisplayLat(center.lat.toFixed(4));
    setDisplayQ(null);
    setDisplayZoom(zoom.toFixed(2));

    if (zoom < ZOOM_SEARCH_ENABLE || resultsDismissedRef.current) return;

    const pointInView = await map.current.project(center);

    const features = map.current.queryRenderedFeatures(pointInView, {
      layers: ["coverage"],
    });

    const totalCount = features.reduce((sum, i) => {
      return sum + i.properties["count"];
    }, 0);

    setImagesCount(totalCount);
  };

  const dismissResults = () => {
    _vaTrack("map.photo_search_dismissed", { event_value: displayZoom });
    resultsDismissedRef.current = true;
    setResultsDismissed(true);
  };

  const restoreResults = () => {
    _vaTrack("map.photo_search_restored", { event_value: displayZoom });
    resultsDismissedRef.current = false;
    setResultsDismissed(false);
    searchCenter();
  };

  useEffect(() => {
    if (map.current) return; // initialize map only once

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: "/map/style.json?streets=v2",
      bounds: geojsonExtent(countyGeoJSON),
      fitBoundsOptions: {
        padding: { top: 75, bottom: 15, left: 15, right: 15 },
      },
      trackResize: false,
    });

    document.addEventListener("webkitfullscreenchange", () => {
      setTimeout(() => map.current.resize(), 50);
    });

    document.addEventListener("fullscreenchange", () => {
      setTimeout(() => map.current.resize(), 50);
    });

    new ResizeObserver(() => map.current.resize()).observe(
      mapContainer.current,
    );

    if (!detectMobile.isMobile()) {
      map.current.addControl(new maplibregl.FullscreenControl(), "top-left");

      map.current.addControl(
        new maplibregl.NavigationControl({ showCompass: false }),
        "top-left",
      );
    }

    map.current.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      "top-left",
    );

    // disable map rotation using right click + drag
    map.current.dragRotate.disable();

    // disable map rotation using touch rotation gesture
    map.current.touchZoomRotate.disableRotation();

    map.current.on("load", () => {
      loadCoverages(map, coverages);

      map.current.addSource("coverage", coverageSource);

      map.current.addLayer(countiesLayer);
      map.current.addLayer(coverageLayer);

      map.current.addSource("county", countySource(countyGeoJSON));
      const firstSymbolLayer = map.current
        .getStyle()
        .layers.find((layer) => layer.type === "symbol");
      map.current.addLayer(countyLayer, firstSymbolLayer?.id);

      map.current.addSource("print-area", {
        type: "geojson",
        data: EMPTY_GEOJSON,
      });
      map.current.addLayer({
        id: "print-area-mask",
        type: "fill",
        source: "print-area",
        filter: ["==", ["get", "kind"], "mask"],
        paint: { "fill-color": "#014156", "fill-opacity": 0.35 },
      });
      map.current.addLayer({
        id: "print-area-outline",
        type: "line",
        source: "print-area",
        filter: ["==", ["get", "kind"], "outline"],
        paint: { "line-color": "#ffffff", "line-width": 2.5 },
      });
      map.current.addLayer({
        id: "print-area-handles",
        type: "circle",
        source: "print-area",
        filter: ["==", ["get", "kind"], "handle"],
        paint: {
          "circle-radius": 5,
          "circle-color": "#ffffff",
          "circle-stroke-color": "#2C758D",
          "circle-stroke-width": 2,
        },
      });
    });
  });

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize

    map.current.on("sourcedata", function (e) {
      setLoading(true);

      if (e.isSourceLoaded) {
        setLoading(false);
      }
    });
  }, [map]);

  const debouncedSearch = useRef(
    debounce(async () => {
      await searchCenter();
    }, 150),
  ).current;

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize

    map.current.on("move", async () => {
      debouncedSearch();
    });
  }, [map]);

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize

    const updateSparseCoverages = () => {
      const view = map.current.getBounds().toArray().flat();
      const cells = cellsInBounds(view);

      // Too zoomed out to know, so leave every year available
      if (cells.length > MAX_MAP_CELLS) {
        setSparseCoverages((prev) => (prev.length === 0 ? prev : []));
        setDenseCoverages((prev) => (prev.length === 0 ? prev : []));
        return;
      }
      // Wait until every cell in view has loaded
      if (cells.some((cell) => !loadedCells.current.has(cell.key))) return;

      const ids = Array.from(
        new Set([
          ...(coverages || []).map((c) => c.id),
          ...footprintsRef.current.map((f) => f.id),
        ]),
      );
      const sparse = sparseCoverageIds(footprintsRef.current, view, ids);
      const dense = ids.filter((id) => !sparse.includes(id));
      setSparseCoverages((prev) =>
        prev.join(",") === sparse.join(",") ? prev : sparse,
      );
      setDenseCoverages((prev) =>
        prev.join(",") === dense.join(",") ? prev : dense,
      );
    };

    const loadCell = (cell) => {
      requestedCells.current.add(cell.key);

      fetchCell(cell)
        .then(({ coverages: loaded }) => {
          loaded.forEach((coverage) => addCoverage(map, coverage));

          footprintsRef.current = [
            ...footprintsRef.current,
            ...prepareFootprints(loaded),
          ];
          setFootprints(footprintsRef.current);
          setCellCoverages((prev) => ({
            ...prev,
            ...Object.fromEntries(loaded.map((c) => [c.id, c])),
          }));
          loadedCells.current.add(cell.key);
          updateSparseCoverages();
        })
        .catch((e) => {
          requestedCells.current.delete(cell.key);
          console.error(e);
        });
    };

    // Resizes fire moveend before the style loads, and coverage layers can't
    // be added until it has
    let mapLoaded = false;

    const loadCellsInView = debounce(() => {
      if (!mapLoaded) return;

      const cells = cellsInBounds(map.current.getBounds().toArray().flat());

      if (cells.length <= MAX_MAP_CELLS) {
        cells
          .filter((cell) => !requestedCells.current.has(cell.key))
          .forEach(loadCell);
      }
      updateSparseCoverages();
    }, 250);

    map.current.on("load", () => {
      mapLoaded = true;
      loadCellsInView();
    });
    map.current.on("moveend", loadCellsInView);
  }, [map]);

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize
    if (roadsActive === null) return;
    if (!map.current.isStyleLoaded()) {
      return;
    }

    map.current.getStyle().layers.forEach((layer) => {
      if (layer.source === "maptiler_planet") {
        map.current.setLayoutProperty(
          layer.id,
          "visibility",
          roadsActive ? "visible" : "none",
        );
      }
    });
  }, [roadsActive]);

  // Labels share the street basemap, so hiding them without imagery
  // underneath leaves an empty map
  useEffect(() => {
    if (!satelliteActive && roadsActive === false) setRoadsActive(true);
  }, [satelliteActive]);

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize
    if (coverageActive === null) return;

    ["coverage", "counties"].forEach((layer) => {
      map.current.setPaintProperty(
        layer,
        "fill-opacity",
        coverageActive ? 0.5 : 0.01,
      );
    });
  }, [coverageActive]);

  useEffect(() => {
    if (!map.current) return;
    if (!lng || !lat) return;

    map.current.jumpTo({ center: [lng, lat], zoom: 16 });
  }, [lng, lat]);

  useEffect(() => {
    const source = map.current && map.current.getSource("print-area");
    if (!source) return;

    source.setData(
      printOpen && printBbox
        ? printAreaGeoJSON(printBbox, { handles: printMode === "box" })
        : EMPTY_GEOJSON,
    );
  }, [printOpen, printMode, printBbox && printBbox.join(",")]);

  useEffect(() => {
    if (!map.current) return;

    if (!printOpen || printMode !== "point" || !printCenter) {
      if (printMarker.current) printMarker.current.remove();
      printMarker.current = null;
      return;
    }

    if (!printMarker.current) {
      printMarker.current = new maplibregl.Marker({
        color: "#2C758D",
        draggable: true,
      })
        .setLngLat(printCenter)
        .addTo(map.current);
      printMarker.current.on("drag", () =>
        setPrintCenter(printMarker.current.getLngLat().toArray()),
      );
      printMarker.current.on("dragend", () => trackPrintAdjusted("pin"));
    } else {
      printMarker.current.setLngLat(printCenter);
    }
  }, [printOpen, printMode, printCenter]);

  // Bring the point-and-radius square into view, clear of the print panel
  useEffect(() => {
    if (!map.current || !printOpen || printMode !== "point" || !printCenter) {
      return;
    }

    const [west, south, east, north] = lngLatBounds(
      squareAroundPoint(printCenter, printDistance),
    );
    const wide = mapContainer.current.clientWidth > 700;
    map.current.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      {
        padding: { top: 60, bottom: 40, right: 40, left: wide ? 360 : 40 },
        maxZoom: 16,
      },
    );
  }, [printOpen, printDistance]);

  useEffect(() => {
    if (!map.current) return;

    if (!printOpen) map.current.getCanvas().style.cursor = "";
  }, [printOpen]);

  useEffect(() => {
    if (!map.current) return; // wait for map to initialize

    let drawStart = null;
    let previousBbox = null;
    let resizing = null;
    let moving = null;

    const project = (lngLat) => map.current.project(lngLat);
    const tolerance = (e) =>
      e.originalEvent.type.startsWith("touch") ? 16 : 8;

    // What a press at this point would grab: a resize handle, the square
    // itself, or nothing (so the map pans)
    const targetAt = (e) => {
      const { mode, bbox } = printState.current;
      if (!bbox) return null;

      if (mode === "box") {
        const handle = squareHandleAt(bbox, e.point, project, tolerance(e));
        if (handle) return { handle };
      }

      return squareContains(bbox, e.point, project) ? { move: true } : null;
    };

    const startDraw = (e) => {
      const { open, mode, bbox, center } = printState.current;
      if (!open) return;
      if (e.originalEvent.target.closest(".maplibregl-marker")) return;

      const target = targetAt(e);
      if (target && target.handle) {
        e.preventDefault();
        resizing = { handle: target.handle, bbox };
      } else if (target && target.move) {
        e.preventDefault();
        moving = {
          start: toMercator(e.lngLat.toArray()),
          bbox,
          center: center && toMercator(center),
        };
      } else if (mode === "box" && (!bbox || e.originalEvent.shiftKey)) {
        e.preventDefault();
        drawStart = e.lngLat.toArray();
        previousBbox = bbox;
      }
    };

    const moveDraw = (e) => {
      if (resizing) {
        setDrawnBbox(
          resizeSquare(
            resizing.bbox,
            resizing.handle,
            e.lngLat.toArray(),
            MIN_DRAWN_SQUARE_MILES,
          ),
        );
      } else if (moving) {
        const [x, y] = toMercator(e.lngLat.toArray());
        const dx = x - moving.start[0];
        const dy = y - moving.start[1];

        if (printState.current.mode === "box") {
          const [xmin, ymin, xmax, ymax] = moving.bbox;
          setDrawnBbox([xmin + dx, ymin + dy, xmax + dx, ymax + dy]);
        } else {
          setPrintCenter(
            fromMercator([moving.center[0] + dx, moving.center[1] + dy]),
          );
        }
      } else if (drawStart) {
        setDrawnBbox(squareFromDrag(drawStart, e.lngLat.toArray()));
      } else if (printState.current.open) {
        const target = targetAt(e);
        map.current.getCanvas().style.cursor = target
          ? target.handle
            ? RESIZE_CURSORS[target.handle]
            : "move"
          : "";
      }
    };

    const endDraw = (e) => {
      if (resizing || moving) {
        trackPrintAdjusted(resizing ? "resize" : "move");
        resizing = null;
        moving = null;
        return;
      }
      if (!drawStart) return;

      const bbox = squareFromDrag(drawStart, e.lngLat.toArray());
      const tooSmall = squareSideMiles(bbox) < MIN_DRAWN_SQUARE_MILES;
      setDrawnBbox(tooSmall ? previousBbox : bbox);
      if (!tooSmall) trackPrintAdjusted("draw");
      drawStart = null;
    };

    const movePoint = (e) => {
      const { open, mode } = printState.current;
      if (open && mode === "point") {
        setPrintCenter(e.lngLat.toArray());
        trackPrintAdjusted("click");
      }
    };

    // Mouseup outside the map canvas never reaches the map's handlers
    window.addEventListener("mouseup", () => {
      resizing = null;
      moving = null;
      drawStart = null;
    });

    map.current.on("mousedown", startDraw);
    map.current.on("touchstart", startDraw);
    map.current.on("mousemove", moveDraw);
    map.current.on("touchmove", moveDraw);
    map.current.on("mouseup", endDraw);
    map.current.on("touchend", endDraw);
    map.current.on("click", movePoint);
  }, [map]);

  // Other counties' coverages only show up once they cover enough of the view
  const countyCoverageIds = new Set((coverages || []).map((c) => c.id));
  const timelineCoverages = allCoverages.filter(
    (c) =>
      countyCoverageIds.has(c.id) ||
      denseCoverages.includes(c.id) ||
      String(c.id) === String(activeCoverage) ||
      String(c.id) === String(displayedCoverage),
  );

  const coverageTimeline = (className, horizontal = false) =>
    timelineCoverages.length > 0 && (
      <CoverageTimeline
        className={className}
        horizontal={horizontal}
        coverages={timelineCoverages}
        checked={satelliteActive}
        activeCoverage={activeCoverage}
        displayedCoverage={displayedCoverage}
        sparseCoverages={sparseCoverages}
        playing={playing}
        canPlay={playSequence.length > 1}
        onToggle={handleSatelliteClick}
        onTogglePlay={handleTogglePlay}
        onSelect={handleTimelineSelect}
      />
    );

  return (
    <div className="md:flex md:space-x-3">
      <div id="map-wrapper" className="md:flex-1 md:min-w-0">
        <div className="search-wrapper">
          <BaseSearchBox
            placeholder="Find an address"
            onSelect={(item) => {
              if (item && item.data) {
                fetch(item.data)
                  .then((response) => response.json())
                  .then((data) => {
                    setLat(data.lat);
                    setLng(data.lng);
                    setDisplayQ(data.address);
                    setPrintCenter([data.lng, data.lat]);
                    setDrawnBbox(
                      squareAroundPoint([data.lng, data.lat], printDistance),
                    );
                  });
              }
            }}
            types={"addresses"}
          />
        </div>

        <div ref={mapContainer} className="map-container">
          <div className="maplibregl-ctrl-group absolute z-50 top-[10px] right-[10px] px-2 py-1 bg-white space-x-3 flex flex-row items-center">
            <LayerToggle
              checked={coverageActive}
              onChange={handleCoverageClick}
            >
              Coverage
            </LayerToggle>
            <LayerToggle
              checked={roadsActive}
              onChange={handleRoadsActiveClick}
              disabled={!satelliteActive}
              title={
                satelliteActive ? undefined : "Turn on imagery to hide labels"
              }
            >
              Labels
            </LayerToggle>
            {allCoverages.length > 0 && (
              <button
                type="button"
                onClick={handlePrintToggle}
                className={`flex items-center space-x-1 text-xs select-none ${
                  printOpen ? "font-bold text-va-blue-700" : "text-gray-700"
                }`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />
                </svg>
                <span>Print</span>
              </button>
            )}
          </div>

          {displayZoom < ZOOM_SEARCH_ENABLE && !hideInstructions && (
            <div>
              <div
                className="shade"
                onClick={() => setHideInstructions(true)}
              ></div>
              <div className="instructions">
                <div
                  onClick={() => {
                    Cookies.set(HID_COUNTY_MAP_TIP_COOKIE_NAME, "true", {
                      expires: 30,
                    });
                    setHideInstructions(true);
                  }}
                >
                  <svg
                    className="close"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6 16.094l-4.157-4.104 4.1-4.141-1.849-1.849-4.105 4.159-4.156-4.102-1.833 1.834 4.161 4.12-4.104 4.157 1.834 1.832 4.118-4.159 4.143 4.102 1.848-1.849z" />
                  </svg>
                </div>
                <svg
                  className="arrow-up"
                  width="100"
                  height="111"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="arrow-up arrow"
                    d="M46.125 19.619c-8.279 17.648-20.822 46.867-16.468 66.583 4.895 22.151 24.899 12.535 41.708 7.549"
                    stroke="#56B3D2"
                    strokeWidth="2.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                  />
                  <path
                    className="arrow-up tail"
                    d="M26.826 22.036C37.716 20.37 43.453 11.102 53.361 7.49c-.631 8.043-1.18 24.408 2.945 31.81"
                    stroke="#56B3D2"
                    strokeWidth="2.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <svg
                  className="no-mobile arrow-coverage"
                  width="276"
                  height="209"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="arrow-coverage arrow"
                    d="M2 201.027c53.67 21.379 129.983-10.887 143.448-70.35 3.53-15.6 8.833-46.238-8.264-55.935-37.504-21.266-54.947 76.974 6.21 64.059 25.292-5.344 45.891-29.029 62.082-47.734 19.228-22.201 35.754-46.59 54.983-68.788"
                    stroke="#56B3D2"
                    strokeWidth="2.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    className="arrow-coverage tail"
                    d="M273.931 49.04c-6.627-15.839 1.007-30.934-2.249-47.04-10.153 7.317-31.416 21.226-44.572 21.696"
                    stroke="#56B3D2"
                    strokeWidth="2.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="heading">Know the current address?</div>
                <div className="subheading">
                  Begin by typing it above, or zoom and move the map to search
                  an area.
                </div>
                <div className="tip">
                  <strong>Tip:</strong> Pick a year on the right to view
                  imagery, or click Coverage to view an overlay of our coverage.
                </div>
              </div>
            </div>
          )}

          {displayZoom >= ZOOM_SEARCH_ENABLE &&
            !resultsDismissed &&
            !printOpen && <div className="crosshair" />}

          {coverageActive && (
            <Legend ramp={displayZoom < 7 ? countiesRamp : coverageRamp} />
          )}

          <div
            className={`lat-lng ${printOpen ? "md:hidden" : ""}`}
            style={{ color: satelliteActive ? "white" : "inherit" }}
          >
            {displayLat}, {displayLng}
          </div>

          <div
            id="results"
            style={{ display: resultsDismissed || printOpen ? "none" : "" }}
          >
            {displayZoom >= ZOOM_SEARCH_ENABLE && imagesCount > 0 && (
              <form action={searchPath} method="get">
                <input type="hidden" name="type" value="address" />
                <input type="hidden" name="lat" value={displayLat || ""} />
                <input type="hidden" name="lng" value={displayLng || ""} />
                <input type="hidden" name="q" value={displayQ || ""} />

                <button disabled={!imagesCount > 0}>Show Nearby Photos</button>
                <a
                  className="dismiss"
                  aria-label="Hide photo search"
                  title="Hide photo search"
                  onClick={dismissResults}
                >
                  &times;
                </a>
              </form>
            )}

            <div
              className="unmapped"
              style={{
                display:
                  loading === false &&
                  imagesCount == 0 &&
                  displayZoom >= ZOOM_SEARCH_ENABLE
                    ? "block"
                    : "none",
              }}
            >
              <a
                className="dismiss"
                aria-label="Hide photo search"
                title="Hide photo search"
                onClick={dismissResults}
              >
                &times;
              </a>

              <div className="message">
                We aren't finding any photos in this location.
              </div>

              {unmappedRollsCount > 0 && (
                <div className="other-rolls">
                  However, there are {unmappedRollsCount} rolls of unmapped film
                  that may contain the photo you are looking for!
                </div>
              )}

              {unmappedRollsCount === 0 && (
                <div className="no-unmapped-rolls">
                  However, there may be photos in other areas in this county.
                  {coverageActive === false && (
                    <a onClick={() => setCoverageActive(true)}>Show Coverage</a>
                  )}
                </div>
              )}

              <div className="button browse-unmapped-film">
                <a href={unmappedPath}>Browse Unmapped Film</a>
              </div>
            </div>
          </div>

          {resultsDismissed &&
            !printOpen &&
            displayZoom >= ZOOM_SEARCH_ENABLE && (
              <a className="restore-results" onClick={restoreResults}>
                Search for photos here
              </a>
            )}
        </div>

        {printOpen && (
          <PrintSelector
            className="mt-2 md:mt-0 md:absolute md:z-50 md:bottom-[10px] md:left-[10px] md:w-80"
            coverage={printCoverage}
            mode={printMode}
            onModeChange={handlePrintModeChange}
            distance={printDistance}
            onDistanceChange={handlePrintDistanceChange}
            bbox={printBbox}
            imageryCoverage={
              printCoverage && printBbox && footprints.length > 0
                ? footprintCoverage(
                    footprints
                      .filter((f) => f.id === printCoverage.id)
                      .map((f) => f.geometry),
                    printBbox,
                  )
                : null
            }
            cartItemsPath={cartItemsPath}
            couponCode={highAltitudePrintCouponCode}
            onClose={() => setPrintOpen(false)}
          />
        )}
      </div>

      {coverageTimeline("md:hidden mt-2", true)}

      {coverageTimeline(
        "hidden md:block w-40 shrink-0 h-[525px] overflow-y-auto bg-white border border-gray-200 rounded px-4 pb-3",
      )}
    </div>
  );
};

import renderer from "../shared/renderer";
renderer(CountyMap);
